import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Permission } from '@ecobox/domain';
import {
  ledgerById,
  permissionsFromClaims,
  type LedgerEntry,
  type OpsSession,
  type SessionEngine,
} from '@ecobox/firebase';
import { useStore } from '@ecobox/ui';
import { useLedger, useSession } from './services';

const EngineContext = createContext<SessionEngine | null>(null);

export function SessionsProvider({
  engine,
  children,
}: {
  engine: SessionEngine;
  children: ReactNode;
}) {
  return <EngineContext.Provider value={engine}>{children}</EngineContext.Provider>;
}

export function useSessionEngine(): SessionEngine {
  const engine = useContext(EngineContext);
  if (!engine) throw new Error('SessionsProvider is missing.');
  return engine;
}

/** Every scan session saved on this phone. */
export function useAllSessions(): readonly OpsSession[] {
  return useStore(useSessionEngine());
}

/** The device ledger by command id: how each scan's command is doing. */
export function useLedgerById(): ReadonlyMap<string, LedgerEntry> {
  const ledger = useLedger();
  return useMemo(() => ledgerById(ledger), [ledger]);
}

/** What the signed-in person's roles allow. The server checks again on every command. */
export function usePermissions(): { has: (p: Permission) => boolean } {
  const { claims } = useSession();
  return useMemo(() => {
    const set = permissionsFromClaims(claims);
    return { has: (p: Permission) => set.has(p) };
  }, [claims]);
}
